import React, {useState, useCallback} from 'react';
import styled, {keyframes, createGlobalStyle} from 'styled-components';
import {TriviaAdd} from './TriviaAdd';
import {TriviaGame} from './TriviaGame';
import {TriviaLeaderboard} from './TriviaLeaderboard';
import {CardFan} from './CardFan';
import {TriviaBackground} from './TriviaBackground';

const TriviaGlobalStyle = createGlobalStyle`
    html, body, #root, #app {
        background: linear-gradient(135deg, #0f0c29, #302b63, #24243e) !important;
        background-attachment: fixed !important;
    }
`;

export const Trivia = () => {
    const [view, setView] = useState('menu');
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authAnswer, setAuthAnswer] = useState('');
    const [authError, setAuthError] = useState(false);

    const goTo = useCallback((v) => {
        if (v === 'add') {
            if (sessionStorage.getItem('trivia_auth') === 'true') {
                setView('add');
            } else {
                setShowAuthModal(true);
                setAuthAnswer('');
                setAuthError(false);
            }
            return;
        }
        setView(v);
    }, []);

    const handleAuth = () => {
        if (authAnswer.trim() === 'כפיר') {
            sessionStorage.setItem('trivia_auth', 'true');
            setShowAuthModal(false);
            setAuthAnswer('');
            setAuthError(false);
            setView('add');
        } else {
            setAuthError(true);
        }
    };

    const handleAuthKey = (e) => {
        if (e.key === 'Enter') handleAuth();
    };

    return (
        <Container>
            <TriviaGlobalStyle/>
            <TriviaBackground/>
            {view === 'menu' && <TriviaMenu onNavigate={goTo}/>}
            {view === 'add' && <TriviaAdd onBack={() => goTo('menu')}/>}
            {view === 'game' && (
                <TriviaGame
                    onBack={() => goTo('menu')}
                    onShowLeaderboard={() => goTo('leaderboard')}
                    onAddQuestion={() => goTo('add')}
                />
            )}
            {view === 'leaderboard' && <TriviaLeaderboard onBack={() => goTo('menu')}/>}

            {showAuthModal && (
                <ModalOverlay onClick={() => setShowAuthModal(false)}>
                    <ModalBox onClick={e => e.stopPropagation()}>
                        <ModalIcon>🔒</ModalIcon>
                        <ModalTitle>שאלת אבטחה</ModalTitle>
                        <ModalQuestion>מי בנה את הדף הזה (שם פרטי)?</ModalQuestion>
                        <ModalInput
                            value={authAnswer}
                            onChange={e => { setAuthAnswer(e.target.value); setAuthError(false); }}
                            onKeyDown={handleAuthKey}
                            placeholder="הקלידו תשובה..."
                            autoFocus
                            $error={authError}
                        />
                        {authError && <ModalError>תשובה לא נכונה, נסו שוב</ModalError>}
                        <ModalBtnRow>
                            <ModalBtn onClick={handleAuth} $primary>אישור</ModalBtn>
                            <ModalBtn onClick={() => setShowAuthModal(false)}>ביטול</ModalBtn>
                        </ModalBtnRow>
                    </ModalBox>
                </ModalOverlay>
            )}
        </Container>
    );
};

const TriviaMenu = ({onNavigate}) => (
    <MenuWrapper>
        <TitleGroup>
            <Title>טריוויה משפחתית</Title>
            <CardFan size="large"/>
            <Subtitle>כמה טוב אתם מכירים את המשפחה?</Subtitle>
        </TitleGroup>

        <ButtonGroup>
            <MenuButton onClick={() => onNavigate('game')} $color="#f59e0b" $glow="rgba(245,158,11,0.4)">
                <BtnIcon>🃏</BtnIcon> שחקו!
            </MenuButton>
            <MenuButton onClick={() => onNavigate('add')} $color="#8b5cf6" $glow="rgba(139,92,246,0.4)">
                <BtnIcon>➕</BtnIcon> הוסיפו שאלה
            </MenuButton>
            <MenuButton onClick={() => onNavigate('leaderboard')} $color="#10b981" $glow="rgba(16,185,129,0.4)">
                <BtnIcon>🏆</BtnIcon> טבלת האלופים
            </MenuButton>
        </ButtonGroup>
    </MenuWrapper>
);

const gradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;


const fadeOverlay = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const popIn = keyframes`
    0% { transform: scale(0.8) translateY(20px); opacity: 0; }
    100% { transform: scale(1) translateY(0); opacity: 1; }
`;

const Container = styled.div`
    position: relative;
    width: 100%;
    min-height: calc(100vh - 45px);
    overflow-x: hidden;
    background: transparent;
    font-family: 'Heebo', sans-serif;
    direction: rtl;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const MenuWrapper = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: calc(100vh - 120px);
    padding: 40px 20px;
    gap: 40px;
    animation: ${slideUp} 0.6s ease-out;
`;

const TitleGroup = styled.div`
    text-align: center;
`;

const Title = styled.h1`
    font-size: clamp(32px, 7vw, 56px);
    font-weight: 900;
    background: linear-gradient(135deg, #f59e0b, #ec4899, #8b5cf6, #f59e0b);
    background-size: 300% 300%;
    animation: ${gradientShift} 4s ease infinite;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    filter: drop-shadow(0 0 20px rgba(245, 158, 11, 0.3));
`;

const Subtitle = styled.p`
    color: rgba(241, 245, 249, 0.7);
    font-size: clamp(14px, 3vw, 20px);
    margin-top: 12px;
    font-weight: 400;
`;

const ButtonGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 320px;
`;

const MenuButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 16px 32px;
    border: 2px solid ${p => p.$color};
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.05);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 20px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.25s ease;
    backdrop-filter: blur(8px);

    &:hover {
        background: ${p => p.$color};
        box-shadow: 0 0 30px ${p => p.$glow}, 0 8px 24px rgba(0, 0, 0, 0.3);
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }
`;

const BtnIcon = styled.span`
    font-size: 24px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: ${fadeOverlay} 0.2s ease;
`;

const ModalBox = styled.div`
    background: linear-gradient(135deg, #1e1b4b, #312e81);
    border: 1px solid rgba(139, 92, 246, 0.4);
    border-radius: 24px;
    padding: 36px 28px;
    max-width: 380px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    animation: ${popIn} 0.3s ease;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalIcon = styled.div`
    font-size: 48px;
`;

const ModalTitle = styled.h3`
    color: #f1f5f9;
    font-size: 22px;
    font-weight: 800;
    margin: 0;
`;

const ModalQuestion = styled.p`
    color: rgba(241, 245, 249, 0.75);
    font-size: 16px;
    text-align: center;
    margin: 0;
    line-height: 1.6;
`;

const ModalInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border: 2px solid ${p => p.$error ? 'rgba(239,68,68,0.6)' : 'rgba(139,92,246,0.4)'};
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.06);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 18px;
    text-align: center;
    direction: rtl;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;
    &:focus { border-color: ${p => p.$error ? '#ef4444' : '#8b5cf6'}; }
    &::placeholder { color: rgba(241, 245, 249, 0.3); }
`;

const ModalError = styled.div`
    color: #fca5a5;
    font-size: 14px;
    font-weight: 500;
`;

const ModalBtnRow = styled.div`
    display: flex;
    gap: 12px;
    width: 100%;
    margin-top: 4px;
`;

const ModalBtn = styled.button`
    flex: 1;
    padding: 12px;
    border: ${p => p.$primary ? 'none' : '1px solid rgba(255,255,255,0.2)'};
    border-radius: 12px;
    background: ${p => p.$primary ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' : 'rgba(255,255,255,0.05)'};
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    &:hover {
        ${p => p.$primary
            ? 'box-shadow: 0 0 20px rgba(139,92,246,0.4);'
            : 'background: rgba(255,255,255,0.1);'}
    }
`;

export default Trivia;
